import { connect } from 'react-redux';
import i18next from 'i18next';
import React, { useEffect, useMemo, useState } from 'react';

import { closeGenericModal, openGenericModal, refreshGenericModal } from '@actions/modal';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

import { ADMIN_FEATURE_PATHS } from '@commons/utils/featurePaths';
import { GENERIC_MODAL_CANCEL_BUTTON } from '@commons/Modals/GenericModal/genericModalActions';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { getConfirmationModal } from '@commons/Modals/ConfirmationModal';
import { getFilteredStoresWithSelectedGroups } from '@commons/ExportModalWithStoresAndPeriod/utils';
import { isForbiddenAPIError } from '@commons/utils/errors';
import { ListView } from '@commons/utils/styledLibraryComponents';
import EmptyState from '@commons/EmptyState';

import DeepsightFiltersButton from '@admin/components/FilterButton';

import { ENUM_QUERY_PARAMS, withListViewQueryParamHookHOC } from '@hooks/useListViewQueryParams';

import { getAuthorizedActions } from '@selectors/featureProps';
import { getClientInfo } from '@selectors/client';

import { supplierProduct as supplierProductService } from '@services/supplierProduct';
import centralService from '@services/central';

import { exportCentralProducts } from './utils/export';
import { getGroupsWithNoneFromStores } from '@admin/products/recipes/utils/storeGroups';
import { getListActions, getRowActions } from './utils/actions';
import { getListColumns } from './utils/columns';
import { getProductIdsToDeactivate, getSupplierProductIdsToDeactivate } from './utils/deactivation';
import { getSspmStepModalConfig } from '@admin/suppliers/supplierProducts/utils/getSspmStepModalConfig';

const FILTERABLE_STRING_PROPERTY_KEYS = ['category', 'subCategory'];

const StoreCentralProducts = (props) => {
  const {
    history,
    storeParams,
    listViewQueryParams,
    setListViewQueryParams,
    activeStores,
    authorizedActions,
    client: { storeName },
    currency,
    closeGenericModal,
    openGenericModal,
    refreshGenericModal,
    showErrorMessage,
    showSuccessMessage,
  } = props;

  const { id: storeId, name: centralKitchenName } = storeParams;

  const [isLoading, setIsLoading] = useState(true);
  const [supplierProducts, setSupplierProducts] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  const [deliveredStoreIds, setDeliveredStoreIds] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);

  const [isAvailabilitiesModalOpened, setIsAvailabilitiesModalOpened] = useState(false);
  const [supplierProductsToUpdate, setSupplierProductsToUpdate] = useState([]);
  const [selectedAvailabilities, setSelectedAvailabilities] = useState({});

  const [filters, setFilters] = useState(null);
  const [advancedFilters, setAdvancedFilters] = useState(null);
  const [applyFilters, setApplyFilters] = useState(true);

  /**
   * Sales points delivered by the central kitchen, matched against the stores of the user so that
   * the availabilities can only be updated inside their perimeter
   */
  const deliveredStores = useMemo(() => {
    const deliveredStoreIdsSet = new Set(deliveredStoreIds);

    return (activeStores ?? [])
      .filter(({ id, isKitchen }) => !isKitchen && deliveredStoreIdsSet.has(id))
      .map((store) => ({ ...store, groups: store.groups ?? [] }));
  }, [activeStores, deliveredStoreIds]);

  const clientStoreNamePlural = useMemo(
    () => getClientStoreNameTranslation(storeName, true).toLowerCase(),
    [storeName],
  );

  const columns = useMemo(() => getListColumns(clientStoreNamePlural), [clientStoreNamePlural]);

  const storesGroups = useMemo(
    () => getGroupsWithNoneFromStores(deliveredStores),
    [deliveredStores],
  );

  const columnsFilterList = useMemo(
    () =>
      columns
        .filter(({ propertyKey }) => FILTERABLE_STRING_PROPERTY_KEYS.includes(propertyKey))
        .map((column) => {
          const distinctValues = [
            ...new Set(
              supplierProducts
                .map((supplierProduct) => supplierProduct[column.propertyKey])
                .filter(Boolean),
            ),
          ].sort((value, otherValue) => value.localeCompare(otherValue));

          return {
            ...column,
            list: distinctValues.map((value, index) => ({
              id: index,
              name: value,
              value,
              [column.propertyKey]: value,
            })),
          };
        }),
    [columns, supplierProducts],
  );

  const displayedSupplierProducts = useMemo(() => {
    if (!advancedFilters?.length) {
      return supplierProducts;
    }

    return advancedFilters.reduce(
      (result, { doFilter, propertyKey, value }) => doFilter(result, propertyKey, value),
      supplierProducts,
    );
  }, [supplierProducts, advancedFilters, applyFilters]);

  useEffect(() => {
    if (!storeId) {
      return;
    }

    (async function loadData() {
      await fetchSupplierProducts();
      await fetchDeliveredStoreIds();
    })();
  }, [storeId]);

  useEffect(() => {
    if (!isAvailabilitiesModalOpened) {
      return;
    }

    const filteredStores = getFilteredStoresWithSelectedGroups(selectedGroups, deliveredStores);

    setSelectedStores(filteredStores);
  }, [selectedGroups, isAvailabilitiesModalOpened]);

  useEffect(() => {
    if (!isAvailabilitiesModalOpened) {
      return;
    }

    refreshGenericModal(getAvailabilitiesModalConfig());
  }, [isAvailabilitiesModalOpened, selectedGroups, selectedStores, selectedAvailabilities]);

  const fetchSupplierProducts = async () => {
    setIsLoading(true);

    try {
      const fetchedSupplierProducts =
        await centralService.getCentralKitchenSupplierProductsByStoreId(storeId);

      setSupplierProducts(fetchedSupplierProducts);
      setSelectedItems([]);
    } catch (error) {
      setSupplierProducts([]);

      showErrorMessage(i18next.t('ADMIN.STORES.CENTRAL_PRODUCTS_LOADING_ERROR'));

      if (isForbiddenAPIError(error)) {
        history.goBack();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDeliveredStoreIds = async () => {
    try {
      const salesPointsByKitchenStoreId = await centralService.getSalesPointsByKitchenStoreIds([
        storeId,
      ]);

      const suppliedSalesPointStores =
        salesPointsByKitchenStoreId[storeId]?.suppliedSalesPointStores ?? [];

      setDeliveredStoreIds(suppliedSalesPointStores.map(({ id }) => id));
    } catch {
      showErrorMessage(i18next.t('ADMIN.STORES.CENTRAL_PRODUCTS_LOADING_ERROR'));
    }
  };

  /**
   * Updates the selected availabilities based on the provided type, key and value
   *
   * @param {string} type  - Type only can be 'toggle' or 'radio'
   * @param {string} key   - The availability to update: 'available', 'hasStock', 'hasDlc' or 'hasTransfer'
   * @param {boolean} value
   */
  const handleAvailabilitiesUpdates = (type, key, value) => {
    const isNotSelected = selectedAvailabilities[key] === undefined;

    if (type === 'toggle') {
      if (isNotSelected) {
        setSelectedAvailabilities({ ...selectedAvailabilities, [key]: true });

        return;
      }

      const updatedAvailabilities = { ...selectedAvailabilities };

      delete updatedAvailabilities[key];

      setSelectedAvailabilities(updatedAvailabilities);

      return;
    }

    setSelectedAvailabilities({ ...selectedAvailabilities, [key]: value });
  };

  const getAvailabilitiesModalConfig = ({
    groups = selectedGroups,
    stores = selectedStores,
  } = {}) =>
    getSspmStepModalConfig({
      selectedAvailabilities,
      userStoresGroups: storesGroups,
      storesChoices: getFilteredStoresWithSelectedGroups(groups, deliveredStores),
      selectedGroups: groups,
      setSelectedGroups,
      selectedStores: stores,
      setSelectedStores,
      handleAvailabilitiesUpdates,
      handleSspmUpdate: handleAvailabilitiesSave,
      handleSspmUpdateModalCloseCleanUp: handleAvailabilitiesModalCloseCleanUp,
    });

  const handleAvailabilitiesModalCloseCleanUp = () => {
    setIsAvailabilitiesModalOpened(false);
    setSupplierProductsToUpdate([]);
    setSelectedAvailabilities({});
  };

  const handleAvailabilitiesUpdate = (concernedSupplierProducts) => {
    setSupplierProductsToUpdate(concernedSupplierProducts);
    setSelectedAvailabilities({});
    setSelectedGroups(storesGroups);
    setSelectedStores(deliveredStores);
    setIsAvailabilitiesModalOpened(true);

    openGenericModal(
      getAvailabilitiesModalConfig({ groups: storesGroups, stores: deliveredStores }),
    );
  };

  const handleAvailabilitiesSave = async () => {
    if (!selectedStores.length || !Object.keys(selectedAvailabilities).length) {
      handleAvailabilitiesModalCloseCleanUp();
      closeGenericModal();

      return;
    }

    setIsLoading(true);

    const { available, hasStock, hasDlc, hasTransfer } = selectedAvailabilities;

    try {
      await supplierProductService.updateBySupplierProductIds(
        supplierProductsToUpdate.map(({ id }) => id),
        selectedStores.map(({ id }) => id),
        available,
        hasStock,
        hasDlc,
        hasTransfer,
      );

      showSuccessMessage(i18next.t('ADMIN.SUPPLIER_PRODUCTS.AVAILABILITIES_SUCCESSFULLY_UPDATED'));
    } catch {
      showErrorMessage(i18next.t('ADMIN.SUPPLIER_PRODUCTS.MODAL_UPDATE_SSPM_ERROR'));
    }

    handleAvailabilitiesModalCloseCleanUp();
    closeGenericModal();

    await fetchSupplierProducts();
  };

  const handleDeactivation = (concernedSupplierProducts) => {
    const productIdsToDeactivate = getProductIdsToDeactivate(concernedSupplierProducts);

    openGenericModal(
      getConfirmationModal({
        title: i18next.t('ADMIN.STORES.CENTRAL_PRODUCTS_DEACTIVATE_ACTION', {
          count: concernedSupplierProducts.length,
        }),
        content: productIdsToDeactivate.length
          ? i18next.t('ADMIN.STORES.CENTRAL_PRODUCTS_DEACTIVATE_LAST_ASSOCIATION_MODAL_CONTENT', {
              count: productIdsToDeactivate.length,
            })
          : i18next.t('ADMIN.STORES.CENTRAL_PRODUCTS_DEACTIVATE_MODAL_CONTENT', {
              count: concernedSupplierProducts.length,
              storeName: clientStoreNamePlural,
            }),
        icon: '/images/inpulse/power-white-small.svg',
        actions: [
          GENERIC_MODAL_CANCEL_BUTTON(),
          {
            key: 1,
            color: 'inpulse-default',
            label: i18next.t('GENERAL.DEACTIVATE'),
            icon: '/images/inpulse/power-white-small.svg',
            handleClick: () =>
              handleCallToDeactivate(concernedSupplierProducts, productIdsToDeactivate),
          },
        ],
      }),
    );
  };

  const handleCallToDeactivate = async (concernedSupplierProducts, productIdsToDeactivate) => {
    setIsLoading(true);

    const supplierProductIdsToDeactivate =
      getSupplierProductIdsToDeactivate(concernedSupplierProducts);

    const deactivations = [
      { ids: productIdsToDeactivate, handleCall: centralService.disableKitchenProductByIds },
      {
        ids: supplierProductIdsToDeactivate,
        handleCall: supplierProductService.disableSupplierProducts,
      },
    ].filter(({ ids }) => !!ids.length);

    let hasSucceeded = false;
    let hasFailed = false;

    for (const { ids, handleCall } of deactivations) {
      try {
        await handleCall(ids);

        hasSucceeded = true;
      } catch {
        hasFailed = true;
      }
    }

    if (hasFailed) {
      showErrorMessage(
        i18next.t(
          hasSucceeded
            ? 'ADMIN.STORES.CENTRAL_PRODUCTS_DEACTIVATE_PARTIAL_ERROR'
            : 'ADMIN.STORES.CENTRAL_PRODUCTS_DEACTIVATE_ERROR',
        ),
      );
    }

    if (!hasFailed) {
      showSuccessMessage(
        i18next.t('ADMIN.STORES.CENTRAL_PRODUCTS_DEACTIVATE_SUCCESS', {
          count: concernedSupplierProducts.length,
        }),
      );
    }

    await fetchSupplierProducts();
  };

  const handleExport = (itemsToExport) =>
    exportCentralProducts(itemsToExport ?? displayedSupplierProducts, currency, centralKitchenName);

  const actions = getListActions({
    selectedItems,
    supplierProducts: displayedSupplierProducts,
    authorizedActions,
    handleAvailabilitiesUpdate,
    handleDeactivation,
    handleExport,
  });

  const rowActions = getRowActions({
    authorizedActions,
    handleAvailabilitiesUpdate,
    handleDeactivation,
  });

  return (
    <ListView
      actions={actions}
      columns={columns}
      data={displayedSupplierProducts}
      defaultCurrentPage={listViewQueryParams[ENUM_QUERY_PARAMS.CURRENT_PAGE]}
      defaultMaxPerPage={listViewQueryParams[ENUM_QUERY_PARAMS.MAX_PER_PAGE]}
      defaultOrderBy={listViewQueryParams[ENUM_QUERY_PARAMS.ORDER_BY]}
      defaultOrderType={listViewQueryParams[ENUM_QUERY_PARAMS.ORDER_TYPE]}
      defaultSearchInput={listViewQueryParams[ENUM_QUERY_PARAMS.SEARCH]}
      handleCurrentPageChange={(input) =>
        setListViewQueryParams[ENUM_QUERY_PARAMS.CURRENT_PAGE](input)
      }
      handleMaxPerPageChange={(input) =>
        setListViewQueryParams[ENUM_QUERY_PARAMS.MAX_PER_PAGE](input)
      }
      handleOrderByChange={(input) => setListViewQueryParams[ENUM_QUERY_PARAMS.ORDER_BY](input)}
      handleOrderTypeChange={(input) => setListViewQueryParams[ENUM_QUERY_PARAMS.ORDER_TYPE](input)}
      handleSearchInputChange={(input) => setListViewQueryParams[ENUM_QUERY_PARAMS.SEARCH](input)}
      isLoading={isLoading}
      padding={'108px 24px 0px 24px'}
      placeholderShape={i18next.t('GENERAL.SEARCH')}
      renderEmptyState={() => (
        <EmptyState label={i18next.t('ADMIN.STORES.CENTRAL_PRODUCTS_EMPTY_STATE_TITLE')} />
      )}
      renderFilterButton={() => (
        <DeepsightFiltersButton
          advancedFilters={advancedFilters}
          applyFilters={applyFilters}
          columnsFilterList={columnsFilterList}
          filters={filters}
          isLoading={isLoading}
          readOnly={isLoading}
          setAdvancedFilters={setAdvancedFilters}
          setApplyFilters={setApplyFilters}
          setFilters={setFilters}
          textFilterButton={i18next.t('GENERAL.LIST_VIEW_FILTER_BUTTON')}
        />
      )}
      rowActions={rowActions}
      setSelectedItems={setSelectedItems}
    />
  );
};

const mapStateToProps = (state) => ({
  activeStores: state.baseReducer.activeStores,
  client: getClientInfo(state.baseReducer.user),
  currency: state.baseReducer.currency,
  authorizedActions: getAuthorizedActions(
    state.baseReducer.userRights,
    ADMIN_FEATURE_PATHS.ADMIN_SUPPLIERS_SUPPLIER_PRODUCTS,
  ),
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  refreshGenericModal: (params) => {
    dispatch(refreshGenericModal(params));
  },
  closeGenericModal: () => {
    dispatch(closeGenericModal());
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withListViewQueryParamHookHOC(StoreCentralProducts));
